import { useMemo, useState } from "react";
import { ChoiceField, NumField } from "@/components/tool/CalcFields";
import { numField } from "@/lib/health/body-profile";
import {
  ACTIVITY,
  type ActivityLevel,
  bmrMifflin,
  type Sex,
  tdee,
} from "@/lib/health/fitness";
import {
  BodyProfileHint,
  useBodyProfileSync,
} from "@/lib/health/use-body-profile";
import { SciStatGrid } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

export function BmrCalc() {
  const locale = getLocale();
  const [sex, setSex] = useState<Sex>("male");
  const [weight, setWeight] = useState("80");
  const [height, setHeight] = useState("180");
  const [age, setAge] = useState("30");
  const [level, setLevel] = useState<ActivityLevel>("moderate");

  const profileSync = useBodyProfileSync({
    apply: (p) => {
      if (p.sex) setSex(p.sex);
      if (p.weightKg) setWeight(String(p.weightKg));
      if (p.heightCm) setHeight(String(p.heightCm));
      if (p.age) setAge(String(p.age));
      if (p.activity) setLevel(p.activity);
    },
    collect: () => ({
      sex,
      weightKg: numField(weight),
      heightCm: numField(height),
      age: numField(age),
      activity: level,
    }),
    deps: [sex, weight, height, age, level],
  });

  const out = useMemo(() => {
    const w = Number(weight);
    const h = Number(height);
    const a = Number(age);
    if (![w, h, a].every(Number.isFinite) || w <= 0 || h <= 0 || a <= 0)
      return null;
    const bmr = bmrMifflin(sex, w, h, a);
    return { bmr, tdee: tdee(bmr, level) };
  }, [sex, weight, height, age, level]);

  const kcal = (n: number) => `${Math.round(n).toLocaleString(locale)} kcal`;

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <ChoiceField
        label={m.h_sex()}
        value={sex}
        onChange={setSex}
        options={[
          { value: "male", label: m.h_male() },
          { value: "female", label: m.h_female() },
        ]}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <NumField
          id="weight"
          label={m.h_weight_kg()}
          value={weight}
          onChange={setWeight}
        />
        <NumField
          id="height"
          label={m.h_height_cm()}
          value={height}
          onChange={setHeight}
        />
        <NumField id="age" label={m.h_age()} value={age} onChange={setAge} />
      </div>
      <ChoiceField
        label={m.h_activity()}
        value={level}
        onChange={setLevel}
        options={(Object.keys(ACTIVITY) as ActivityLevel[]).map((k) => ({
          value: k,
          label: m[`h_act_${k}` as "h_act_moderate"](),
        }))}
      />

      {out ? (
        <>
          <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.bmr_tdee_label()}
            </span>
            <span
              data-testid="bmr-tdee"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {kcal(out.tdee)}
            </span>
          </div>
          <SciStatGrid
            items={[
              { label: m.bmr_label(), value: kcal(out.bmr) },
              { label: m.bmr_lose(), value: kcal(out.tdee - 500) },
              { label: m.bmr_gain(), value: kcal(out.tdee + 500) },
            ]}
          />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{m.h_hint()}</p>
      )}
      <BodyProfileHint {...profileSync} />
    </div>
  );
}
